/**
 * @file capture/captureProfile.ts
 * @brief Boardroom mic constraints — the browser twin of the native 'raw' profile.
 *
 * @details
 * `docs/architecture/boardroom-pipeline.md` §0 defines boardroom capture as a
 * multi-party RAW profile: on device we disable single-talker voice processing
 * (iOS `setVoiceProcessingEnabled:NO`, Android `UNPROCESSED`) so AGC/AEC/noise
 * suppression cannot duck the secondary speakers and collapse the meeting to one
 * detected voice. The browser recorder must match, so every flag is set OFF here:
 *
 * - `echoCancellation` — the recorder never plays audio back, so AEC has nothing
 *   of ours to cancel. What it DOES cancel is meeting audio arriving through the
 *   laptop speaker (remote Zoom/Teams participants), silently erasing half the
 *   room from the recording.
 * - `noiseSuppression` — already off on the default path; kept off here so the
 *   mic-picker path stops diverging.
 * - `autoGainControl` — level-flattens every speaker toward the same loudness,
 *   which is exactly the cue diarization separates voices by.
 *
 * Flags are stated EXPLICITLY rather than omitted: a bare `{ deviceId }` leaves
 * Chrome's defaults in force (AEC/NS/AGC all on), which is how picking a mic used
 * to silently re-enable the processing the default path deliberately disabled.
 *
 * AGC off means quieter capture (no browser-side gain stage exists to compensate,
 * unlike native Table Mode's 2.5x) — `micMeter` ceiling is tuned for it.
 *
 * @author DES
 * @date 02-08-2026
 */

/** All browser voice processing off — natural per-speaker levels for diarization. */
export const RAW_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/**
 * Audio constraints for boardroom capture, optionally pinned to a chosen device.
 * Always spreads the raw profile so both paths capture identically.
 */
export function boardroomAudioConstraints(
  deviceId?: string,
): MediaTrackConstraints {
  return deviceId
    ? { ...RAW_CAPTURE_CONSTRAINTS, deviceId: { exact: deviceId } }
    : { ...RAW_CAPTURE_CONSTRAINTS };
}
